/**
 * AOV Cart Drawer Enhancements
 *
 * Extends Dawn's cart drawer with:
 * - Recommendation "Add" buttons
 * - Bundle swap logic
 * - Add-to-cart form intercept (opens drawer instead of notification)
 *
 * Relies on Dawn's existing cart.js, pub/sub system, and section rendering.
 * The progress bar and recommendations re-render server-side via Liquid
 * when Dawn's section rendering fires on cart update.
 */

(function () {
  'use strict';

  /**
   * Handle "Add" buttons in recommendations
   */
  function handleRecAdd(e) {
    const btn = e.target.closest('[data-aov-add-variant]');
    if (!btn) return;

    const variantId = btn.dataset.aovAddVariant;
    if (!variantId) return;

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Add failed');
        return res.json();
      })
      .then(() => {
        btn.textContent = 'Added!';
        // Trigger Dawn's cart update pub/sub to re-render drawer
        if (typeof publish !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'aov-cart' });
        } else {
          // Fallback: reload the drawer section
          reloadDrawerSection();
        }
        // Update cart icon bubble
        refreshCartBubble();
      })
      .catch((err) => {
        console.error('AOV add error:', err);
        btn.textContent = originalText;
        btn.disabled = false;
      });
  }

  /**
   * Handle bundle swap
   */
  function handleBundleSwap(e) {
    const btn = e.target.closest('[data-aov-bundle-swap]');
    if (!btn) return;

    const wrapper = btn.closest('[data-bundle-upsell]');
    if (!wrapper) return;

    const bundleVariantId = parseInt(wrapper.dataset.bundleVariant, 10);
    const keysToRemove = wrapper.dataset.removeKeys.split(',');

    btn.disabled = true;
    btn.textContent = 'Upgrading...';

    // Build updates object: set all matched items to 0
    const updates = {};
    keysToRemove.forEach((key) => {
      updates[key] = 0;
    });

    // Remove items, then add bundle
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Remove failed');
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: bundleVariantId, quantity: 1 }),
        });
      })
      .then((res) => {
        if (!res.ok) throw new Error('Add bundle failed');
        return res.json();
      })
      .then(() => {
        if (typeof publish !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'aov-cart' });
        } else {
          reloadDrawerSection();
        }
        refreshCartBubble();
      })
      .catch((err) => {
        console.error('Bundle swap error:', err);
        btn.disabled = false;
        btn.textContent = 'Upgrade to Bundle';
      });
  }

  /* NOTE: Dawn natively handles ATC form intercept and cart link
     drawer opening when cart type is set to "drawer" in theme settings.
     No custom intercept needed here. */

  /**
   * Fallback: reload cart drawer section HTML
   */
  function reloadDrawerSection() {
    fetch('/?section_id=cart-drawer')
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Replace drawer items
        const newItems = doc.querySelector('cart-drawer-items');
        const currentItems = document.querySelector('cart-drawer-items');
        if (newItems && currentItems) {
          currentItems.replaceWith(newItems);
        }

        // Replace footer
        const newFooter = doc.querySelector('.cart-drawer__footer');
        const currentFooter = document.querySelector('.cart-drawer__footer');
        if (newFooter && currentFooter) {
          currentFooter.replaceWith(newFooter);
        }

        // Replace progress bar
        const newProgress = doc.querySelector('[data-progress-bar]');
        const currentProgress = document.querySelector('[data-progress-bar]');
        if (newProgress && currentProgress) {
          currentProgress.replaceWith(newProgress);
        }
      })
      .catch((err) => console.error('Drawer reload error:', err));
  }

  /**
   * Refresh the cart icon bubble count
   */
  function refreshCartBubble() {
    fetch('/?section_id=cart-icon-bubble')
      .then((res) => res.text())
      .then((html) => {
        const el = document.getElementById('cart-icon-bubble');
        if (el) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newBubble = doc.querySelector('.shopify-section');
          if (newBubble) el.innerHTML = newBubble.innerHTML;
        }
      })
      .catch(() => {});
  }

  // --- Discount Code Handling ---

  const DISCOUNT_STORAGE_KEY = 'cart_discount_code';

  /**
   * Apply a discount code via Shopify's /discount/ URL redirect approach.
   * This sets the discount on the checkout. We then verify by fetching /cart.js.
   */
  function applyDiscountCode(code) {
    const applyBtn = document.getElementById('CartDrawer-DiscountApply');
    const feedback = document.getElementById('CartDrawer-DiscountFeedback');
    const input = document.getElementById('CartDrawer-DiscountInput');

    if (!code || !code.trim()) {
      showDiscountFeedback('Please enter a discount code.', 'error');
      return;
    }

    code = code.trim().toUpperCase();

    if (applyBtn) {
      applyBtn.disabled = true;
      applyBtn.textContent = 'Applying...';
    }

    // Use the /discount/ endpoint to apply the code
    fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      redirect: 'follow',
    })
      .then(() => {
        // After applying, check the cart for discount info
        return fetch('/cart.js', {
          headers: { 'Accept': 'application/json' },
        });
      })
      .then((res) => res.json())
      .then((cart) => {
        // Check if any cart-level discount was applied
        const hasCartDiscount = cart.cart_level_discount_applications &&
          cart.cart_level_discount_applications.length > 0;

        // Check if any line item has a discount
        const hasLineDiscount = cart.items && cart.items.some((item) =>
          item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0
        );

        if (hasCartDiscount || hasLineDiscount) {
          // Discount applied successfully
          localStorage.setItem(DISCOUNT_STORAGE_KEY, code);
          showDiscountApplied(code);
          showDiscountFeedback('Discount applied!', 'success');

          // Re-render the drawer to show updated prices
          reloadDrawerSection();
          refreshCartBubble();
        } else {
          // No discount appeared in cart - code is invalid or doesn't apply
          // Clear it so it doesn't persist to checkout
          fetch('/discount/clear', { method: 'GET', redirect: 'follow' }).catch(() => {});
          showDiscountFeedback('Invalid discount code. Please try again.', 'error');
        }

        if (input) input.value = '';
      })
      .catch((err) => {
        console.error('Discount apply error:', err);
        showDiscountFeedback('Could not apply code. Please try again.', 'error');
      })
      .finally(() => {
        if (applyBtn) {
          applyBtn.disabled = false;
          applyBtn.textContent = 'Apply';
        }
      });
  }

  /**
   * Remove the applied discount code
   */
  function removeDiscountCode() {
    const removeBtn = document.getElementById('CartDrawer-DiscountRemove');
    if (removeBtn) removeBtn.disabled = true;

    // Clear the discount by hitting /discount/ with an empty code
    fetch('/discount/clear', { method: 'GET', redirect: 'follow' })
      .then(() => {
        localStorage.removeItem(DISCOUNT_STORAGE_KEY);
        hideDiscountApplied();
        showDiscountFeedback('', '');

        // Re-render the drawer
        reloadDrawerSection();
        refreshCartBubble();
      })
      .catch((err) => {
        console.error('Discount remove error:', err);
      })
      .finally(() => {
        if (removeBtn) removeBtn.disabled = false;
      });
  }

  /**
   * Show the applied discount tag
   */
  function showDiscountApplied(code) {
    const appliedEl = document.getElementById('CartDrawer-DiscountApplied');
    const codeEl = document.getElementById('CartDrawer-DiscountCode');
    const formEl = document.getElementById('CartDrawer-DiscountForm');

    if (appliedEl) appliedEl.style.display = 'flex';
    if (codeEl) codeEl.textContent = code;
    if (formEl) formEl.style.display = 'none';
  }

  /**
   * Hide the applied discount tag, show the form
   */
  function hideDiscountApplied() {
    const appliedEl = document.getElementById('CartDrawer-DiscountApplied');
    const formEl = document.getElementById('CartDrawer-DiscountForm');

    if (appliedEl) appliedEl.style.display = 'none';
    if (formEl) formEl.style.display = 'block';
  }

  /**
   * Show feedback message
   */
  function showDiscountFeedback(message, type) {
    const feedback = document.getElementById('CartDrawer-DiscountFeedback');
    if (!feedback) return;

    feedback.textContent = message;
    feedback.className = 'cart-discount__feedback';
    if (type === 'success') {
      feedback.classList.add('cart-discount__feedback--success');
    } else if (type === 'error') {
      feedback.classList.add('cart-discount__feedback--error');
    }

    // Auto-clear success messages after 3 seconds
    if (type === 'success' && message) {
      setTimeout(() => {
        if (feedback.textContent === message) {
          feedback.textContent = '';
          feedback.className = 'cart-discount__feedback';
        }
      }, 3000);
    }
  }

  /**
   * Restore discount state from localStorage on page load
   */
  function restoreDiscountState() {
    const savedCode = localStorage.getItem(DISCOUNT_STORAGE_KEY);
    if (savedCode) {
      showDiscountApplied(savedCode);
    }
  }

  /**
   * Handle discount-related clicks (delegated)
   */
  function handleDiscountClick(e) {
    // Apply button
    if (e.target.closest('#CartDrawer-DiscountApply')) {
      const input = document.getElementById('CartDrawer-DiscountInput');
      if (input) applyDiscountCode(input.value);
      return;
    }

    // Remove button
    if (e.target.closest('#CartDrawer-DiscountRemove')) {
      removeDiscountCode();
      return;
    }
  }

  /**
   * Handle Enter key in discount input
   */
  function handleDiscountKeydown(e) {
    if (e.target.id === 'CartDrawer-DiscountInput' && e.key === 'Enter') {
      e.preventDefault();
      applyDiscountCode(e.target.value);
    }
  }

  // --- Initialize ---

  // Delegated click handlers (work after section re-renders)
  document.addEventListener('click', handleRecAdd);
  document.addEventListener('click', handleBundleSwap);
  document.addEventListener('click', handleDiscountClick);
  document.addEventListener('keydown', handleDiscountKeydown);

  // Restore discount state on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreDiscountState);
  } else {
    restoreDiscountState();
  }
})();
